import React, { useState, useEffect, useRef } from "react";
import { Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { useNavigate, useLocation } from "react-router-dom";
import ProfileUpdate from "../Pages/ProfileUpdate";
import "./Components.css";
import { setProfileShow, setProfileBarShow } from "../redux/notificationSlice";
import { useDispatch, useSelector } from "react-redux";
import { getUserProfileDetails } from "../Services/ShowsApi";
import { getConnections } from "../Services/Connection";
import { setData } from "../redux/userSlice";
import ProfileMenu from './ProfileMenu';
import SocialInviteModal from './SocialInviteModal';

const Header = ({ headerTabRef }) => {
  const info = useSelector((state) => state.info)
  const user = useSelector((state) => state.user || {});
  const value = useSelector((state) => state?.value);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [profileImg, setProfileImg] = useState(() => {
    const cached = localStorage.getItem('user_profile_img');
    return cached || "";
  })
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [showSocialInvitePopup, setShowSocialInvitePopup] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const headerRightRef = useRef(null);

  const open = Boolean(anchorEl);

  const handleClick = () => {
    setIsProfileMenuOpen((prev) => !prev);
  }
  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (value?.searchPageLoad !== undefined) {
          try {
            const connectionsResponse = await getConnections(localStorage.getItem('user_id'), 1, 1000);
            const { starredConnectionCount = 0, nonStarredConnectionCount = 0 } = connectionsResponse || {};
            const accurateConnectionCount = starredConnectionCount + nonStarredConnectionCount;
            
            const cachedData = localStorage.getItem('user_profile_data');
            if (cachedData) {
              const userData = JSON.parse(cachedData);
              userData.connectionCount = accurateConnectionCount;
              localStorage.setItem('user_profile_data', JSON.stringify(userData));
              dispatch(setData(userData));
              return;
            }
          } catch (connectionsError) {
            console.error('Failed to fetch fresh connections:', connectionsError);
          }
        }
        
        const cachedData = localStorage.getItem('user_profile_data');
        const cachedImg = localStorage.getItem('user_profile_img');
        
        if (cachedData) {
          const userData = JSON.parse(cachedData);
          const imageToUse = userData.ProfileImageKey || cachedImg;
          if (imageToUse) {
            setProfileImg(imageToUse);
            if (userData.ProfileImageKey && !cachedImg) {
              localStorage.setItem('user_profile_img', userData.ProfileImageKey);
            }
            if (!userData.ProfileImageKey && cachedImg) {
              userData.ProfileImageKey = cachedImg;
            }
          } else {
            console.log('[Header] No ProfileImageKey in cached data or cachedImg');
          }
          dispatch(setData(userData));
          return;
        }

        if (cachedImg && !profileImg) {
          setProfileImg(cachedImg);
        }

        const { data } = await getUserProfileDetails("UserID");
        const userData = data?.body?.Data?.[0];
        if (userData) {
          console.log('User data from API:', userData);
          console.log('Connection count from API:', userData.connectionCount);
          if (userData.ProfileImageKey) {
            setProfileImg(userData.ProfileImageKey);
            localStorage.setItem('user_profile_img', userData.ProfileImageKey);
          } else {
            console.log('[Header] No ProfileImageKey in API response');
          }
          
          // Fetch accurate connection count from connections API
          try {
            const connectionsResponse = await getConnections(localStorage.getItem('user_id'), 1, 1000);
            const { starredConnectionCount = 0, nonStarredConnectionCount = 0 } = connectionsResponse || {};
            const accurateConnectionCount = starredConnectionCount + nonStarredConnectionCount;
            console.log('Accurate connection count from connections API:', accurateConnectionCount);
            
            const finalUserData = {
              ...userData,
              connectionCount: accurateConnectionCount
            };
            
            localStorage.setItem('user_profile_data', JSON.stringify(finalUserData));
            dispatch(setData(finalUserData));
          } catch (connectionsError) {
            console.error('Failed to fetch connections for count:', connectionsError);
            localStorage.setItem('user_profile_data', JSON.stringify(userData));
            dispatch(setData(userData));
          }
        } else {
          console.log('[Header] No userData found in API response');
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }
    };

    fetchUserProfile();

  }, [value?.searchPageLoad, user?.Collaborate, location.pathname])

  useEffect(() => {
    if (user?.ProfileImageKey) {
      setProfileImg(user.ProfileImageKey);
      localStorage.setItem('user_profile_img', user.ProfileImageKey);
      const cachedData = localStorage.getItem('user_profile_data');
      if (cachedData) {
        const userData = JSON.parse(cachedData);
        userData.ProfileImageKey = user.ProfileImageKey;
        localStorage.setItem('user_profile_data', JSON.stringify(userData));
      }
    }
  }, [user?.ProfileImageKey])

  useEffect(() => {
    const checkLocalStorage = () => {
      const cachedImg = localStorage.getItem('user_profile_img');
      if (cachedImg && cachedImg !== profileImg) {
        setProfileImg(cachedImg);
      }
    };
    
    checkLocalStorage();
    const timeout = setTimeout(checkLocalStorage, 500);
    
    return () => clearTimeout(timeout);
  }, [location.pathname])

  const handleLogout = () => {
    setIsProfileMenuOpen(false);
    localStorage.clear();
    navigate("/login");
  };

  const handleProfile = () => {
    setIsProfileMenuOpen(false);
  };

  const isAuthenticated = !!localStorage.getItem("user_id");
  const excludedPaths = ["/meeting", "/signin", "/login", "/signup", "/left-meeting", "/setup-mfa", "/"];

  if (!isAuthenticated || excludedPaths.includes(location.pathname)) {
    return null;
  }

  return (
    <header className="basic-header" ref={headerTabRef}>
      <div className="header-content">
        <button
          type="button"
          className="header-right"
          onClick={handleClick}
          style={{ position: 'relative', background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer' }}
          ref={headerRightRef}
        >
          {profileImg ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img 
                src={profileImg} 
                alt="Profile" 
                style={{ display: 'block', borderRadius: '50%' }}
                onError={(e) => {
                  setProfileImg("");
                }}
                onLoad={() => {
                  console.log('[Header] Image loaded successfully and rendered:', profileImg);
                }}
              />
              <span style={{
                position: 'absolute',
                width: '1.2vh',
                height: '1.2vh',
                borderRadius: '50%',
                background: user?.Collaborate === 1 ? '#19C900' : '#FFA500',
                bottom: '-0.1vh',
                right: '-0.1vh',
                opacity: 1,
                zIndex: 2
              }} />
            </div>
          ) : (
            <PersonRoundedIcon className="profile-icon" />
          )}
        </button>
        <ProfileMenu
          isOpen={isProfileMenuOpen}
          onClose={() => setIsProfileMenuOpen(false)}
          connectionCount={user?.connectionCount || 0}
          UserID={user.UserID}
          Collaborate={user.Collaborate}
          onOpenSocialInvite={() => setShowSocialInvitePopup(true)}
        />
        <SocialInviteModal
          show={showSocialInvitePopup}
          onClose={() => setShowSocialInvitePopup(false)}
        />
      </div>
      {/* <ProfileUpdate open={openProfileDialog} onClose={() => setOpenProfileDialog(false)} /> */}
    </header>
  );
};

export default Header;